import type { SnapshotIn } from "@gadgetinc/mobx-quick-tree";
import { satisfies } from "compare-versions";
import { klona } from "klona";
import { isObject, memoize, mergeWith, sortBy, unionWith } from "lodash";
import type { DeepReadonly } from "ts-essentials";
import type { ModelField } from "../../database/ModelField";
import { FieldType } from "../../type-system/FieldType";
import { assert } from "../../utils";
import { connectionFieldKey, type ConnectionFieldKeyFieldT } from "../connectionFieldKey";
import type { ConnectionModelSpec } from "../utils";
import type { ShopifyApiVersion } from "./ShopifyApiVersion";
import { ShopifyDominion } from "./ShopifyDominion";
import {
  shopifyModelSpecForVersion,
  type GraphQLListSyncConfig,
  type GraphQLNodeListSyncConfig,
  type GraphQLSubListSyncConfig,
  type ShopifySyncConfig,
} from "./ShopifyModelMetadata";

// Intermediate type that does not include selection because we build it when generating the spec
type ShopifySyncConfigDefinition =
  | Exclude<ShopifySyncConfig, GraphQLListSyncConfig | GraphQLSubListSyncConfig | GraphQLNodeListSyncConfig>
  | Omit<GraphQLListSyncConfig, "selection">
  | Omit<GraphQLSubListSyncConfig, "selection">
  | Omit<GraphQLNodeListSyncConfig, "selection">;

type ConnectionModelDefinition = { syncConfig: ShopifySyncConfigDefinition | null } & Omit<ConnectionModelSpec, "syncConfig" | "key">;

type ModelVersions = {
  [key in ShopifyApiVersion]: ConnectionModelDefinition;
};

type AtLeastOneModelVersion<T extends ModelVersions, U = { [K in keyof T]: Pick<T, K> }> = U[keyof U] &
  Partial<{
    [key in ShopifyApiVersion]: Partial<ConnectionModelDefinition>;
  }>;

export type ConnectionModelSpecVersion = {
  key: string;
  /**
   * If not provided, the Shopify model will not be indexed for search.
   */
  defaultSearchableApiIdentifiers?: string[];
  /**
   * If not provided, only the fields that must be filterable will be indexed for filtering and sorting.
   */
  defaultFilterableApiIdentifiers?: string[];
} & AtLeastOneModelVersion<ModelVersions>;

const definedAndMatches = <T>(a: T, b: T) => {
  if (a === undefined || b === undefined) {
    return false;
  }

  return a === b;
};

export const buildConnectionSpec = memoize(
  (
    versions: ConnectionModelSpecVersion,
    untilVersion: ShopifyApiVersion,
    currentFrameworkVersionConstraint?: string
  ): ConnectionModelSpec | null => {
    const sorted = sortBy(
      Object.entries(versions).filter(([apiVersion]) => apiVersion <= untilVersion),
      ([apiVersion]) => apiVersion
    );

    const [first, ...rest] = sorted;
    let result: ConnectionModelDefinition;

    if (!first) {
      return null;
    }

    const firstFieldKeyTypeMap: Map<string, FieldType> = new Map();

    for (const version of sorted) {
      const spec = version[1];

      if (isObject(spec) && "fields" in spec && spec.fields) {
        for (const field of spec.fields) {
          if (field.isDeleted) {
            continue;
          }

          const fieldKey = connectionFieldKey(
            { key: versions.key, name: versions.key } as { readonly key: string; readonly name: string },
            field as ConnectionFieldKeyFieldT
          );
          if (!firstFieldKeyTypeMap.has(fieldKey)) {
            firstFieldKeyTypeMap.set(fieldKey, field.fieldType);
          }
        }
      }
    }

    if (rest.length == 0) {
      result = klona(first[1]) as ConnectionModelDefinition;
    } else {
      result = mergeWith({}, first[1], ...rest.map((spec) => spec[1]), function checkKeys(targetValue: any, sourceValue: any, key: string) {
        if (Array.isArray(targetValue) && Array.isArray(sourceValue)) {
          return unionWith(sourceValue, targetValue, (a, b) => {
            const matches =
              definedAndMatches(a.key, b.key) ||
              definedAndMatches(
                a.contributorConfiguration?.[ShopifyDominion.id]?.shopifyFieldKey,
                b.contributorConfiguration?.[ShopifyDominion.id]?.shopifyFieldKey
              ) ||
              definedAndMatches(
                a.contributorConfiguration?.[ShopifyDominion.id]?.graphQLFieldKey,
                b.contributorConfiguration?.[ShopifyDominion.id]?.graphQLFieldKey
              ) ||
              definedAndMatches(a.apiIdentifer, b.apiIdentifer) ||
              definedAndMatches(a.name, b.name);

            return matches;
          }).filter((item) => {
            if ("isDeleted" in item) {
              return !item.isDeleted;
            }

            return true;
          });
        } else if (key === "syncConfig") {
          return sourceValue;
        }
      });
    }

    if (result.fields) {
      (result as any).fields = result.fields
        .filter((item) => {
          if (!("frameworkVersionConstraint" in item)) return true;
          // if an item has a frameworkVersionConstraint and a currentFrameworkVersionConstraint isn't provided we will not include it
          if (!currentFrameworkVersionConstraint) return false;

          const constraint = item.frameworkVersionConstraint as string;
          return satisfies(currentFrameworkVersionConstraint, constraint);
        })
        // if the field type has changed, we need to include the type in the field key to avoid conflicts
        .map((item) => {
          const fieldKey = connectionFieldKey({ key: versions.key, name: versions.key }, item as ConnectionFieldKeyFieldT);
          const firstFieldKeyType = firstFieldKeyTypeMap.get(fieldKey);

          if (firstFieldKeyType && firstFieldKeyType !== item.fieldType) {
            return {
              ...item,
              includeTypeInFieldKey: true,
            };
          }

          return item;
        });
    }

    let connectionSpec: ConnectionModelSpec;
    if (result.fields && result.syncConfig && ["graphql_list", "graphql_sublist", "graphql_node_list"].includes(result.syncConfig.type)) {
      const additionalSelection = (result.syncConfig as GraphQLListSyncConfig | GraphQLSubListSyncConfig | GraphQLNodeListSyncConfig)
        .additionalSelection;
      connectionSpec = {
        ...result,
        key: versions.key,
        defaultSearchableApiIdentifiers: versions.defaultSearchableApiIdentifiers,
        defaultFilterableApiIdentifiers: versions.defaultFilterableApiIdentifiers,
        syncConfig: {
          ...result.syncConfig,
          selection: (
            shopifyApiVersion: ShopifyApiVersion,
            enabledModelKeys: string[],
            requestedScopes: string[],
            opts?: { removeGraphQLSelections?: string[] }
          ) =>
            buildSelectionFromFields(shopifyApiVersion, result.fields, enabledModelKeys, requestedScopes, {
              additionalSelection,
              removeGraphQLSelections: opts?.removeGraphQLSelections,
            }),
        } as ShopifySyncConfig,
      };
    } else {
      connectionSpec = {
        ...result,
        key: versions.key,
        defaultSearchableApiIdentifiers: versions.defaultSearchableApiIdentifiers,
        defaultFilterableApiIdentifiers: versions.defaultFilterableApiIdentifiers,
      } as ConnectionModelSpec;
    }

    return connectionSpec;
  },
  (versions, untilVersion, currentFrameworkVersionConstraint) =>
    `${versions.key}//${untilVersion}//${currentFrameworkVersionConstraint ?? "no-framework-version"}`
);

export const buildSelectionFromFields = (
  shopifyApiVersion: ShopifyApiVersion,
  fields: DeepReadonly<
    (SnapshotIn<typeof ModelField> & {
      isDeleted?: boolean;
      shouldConvergeField?: (enabledModelKeys: string[], requestedScopes: string[]) => boolean;
    })[]
  >,
  enabledModelKeys: string[],
  grantedScopes: string[],
  opts?: { additionalSelection?: string; skipGraphQLFragments?: string[]; removeGraphQLSelections?: string[] }
) => {
  let gql = "";
  const fragments: Record<string, string[]> = {};

  for (const field of fields) {
    // if we have a HasMany or HasManyThrough field then do not try to generate a selection for it as these are handled as subresources syncs that have their own graphql queries
    if (field.fieldType === FieldType.HasMany || field.fieldType === FieldType.HasManyThrough) {
      if (
        !(
          field.contributorConfiguration?.[ShopifyDominion.id]?.selectForHasMany &&
          enabledModelKeys.includes(field.configuration?.relatedModelKey)
        )
      ) {
        continue;
      }
    }

    if (field.fieldType === FieldType.HasOne || field.fieldType === FieldType.BelongsTo) {
      if (!enabledModelKeys.includes(field.configuration?.relatedModelKey)) {
        continue;
      }
    }

    if (field.contributorConfiguration && field.contributorConfiguration[ShopifyDominion.id]) {
      const graphQLFieldKey = field.contributorConfiguration[ShopifyDominion.id]?.graphQLFieldKey;
      const graphQLFragments = field.contributorConfiguration[ShopifyDominion.id]?.graphQLFragments;
      const graphQLSelection = field.contributorConfiguration[ShopifyDominion.id]?.graphQLSelection;
      const requiredScopes = field.contributorConfiguration[ShopifyDominion.id]?.requiredScopes;

      const inheritSelectionFrom = field.contributorConfiguration[ShopifyDominion.id]?.inheritSelectionFrom;
      const skipGraphQLFragments = Array.from(field.contributorConfiguration[ShopifyDominion.id]?.skipGraphQLFragments ?? []);

      if (requiredScopes && !requiredScopes.every((scope) => grantedScopes.includes(scope))) {
        continue;
      }

      if (!graphQLFieldKey || !!field.contributorConfiguration[ShopifyDominion.id]?.skipGraphQLSelection) {
        continue;
      }

      if (field.shouldConvergeField && !field.shouldConvergeField(enabledModelKeys, grantedScopes)) {
        continue;
      }

      let fieldQuery = `${graphQLFieldKey}${graphQLSelection ? ` {\n${graphQLSelection}\n}` : ""}`;

      if (graphQLSelection && opts?.removeGraphQLSelections) {
        // Check if we need to remove specific nested selections
        let filteredSelection = graphQLSelection;
        for (const removalKey of opts.removeGraphQLSelections) {
          // Simply filter out any lines that contain the removal key
          filteredSelection = filteredSelection
            .split("\n")
            .filter((line) => !line.includes(removalKey))
            .join("\n");
        }

        fieldQuery = `${graphQLFieldKey} {\n${filteredSelection}\n}`;
      }

      if (inheritSelectionFrom) {
        const inheritedSpec = assert(shopifyModelSpecForVersion(inheritSelectionFrom, shopifyApiVersion), "Inherited metadata not found");

        if (field.fieldType === FieldType.HasMany) {
          gql += `${graphQLFieldKey} {\n${
            buildSelectionFromFields(shopifyApiVersion, inheritedSpec.fields, enabledModelKeys, grantedScopes, { skipGraphQLFragments })
              .selection
          }\n}`;
        } else {
          gql += buildSelectionFromFields(shopifyApiVersion, inheritedSpec.fields, enabledModelKeys, grantedScopes, {
            skipGraphQLFragments,
          }).selection;
        }
      } else if (graphQLFragments) {
        for (const fragment of graphQLFragments) {
          let fragmentType: string;
          let fragmentSelection: string;

          if (isObject(fragment)) {
            if (opts?.skipGraphQLFragments && opts.skipGraphQLFragments.includes(fragment.type)) {
              continue;
            }
            fragmentType = fragment.type;
            fragmentSelection = fragment.selection;
          } else {
            if (opts?.skipGraphQLFragments && opts.skipGraphQLFragments.includes(fragment)) {
              continue;
            }
            fragmentType = fragment;
            fragmentSelection = fieldQuery;
          }

          if (fragments[fragmentType]) {
            fragments[fragmentType].push(fragmentSelection);
          } else {
            fragments[fragmentType] = [fragmentSelection];
          }
        }
      } else {
        gql += fieldQuery;
      }

      gql += "\n";
    }
  }

  for (const [key, graphQLFields] of Object.entries(fragments)) {
    gql += `... on ${key} {
      ${graphQLFields.join("\n")}
    }`;
  }

  if (opts?.additionalSelection) {
    gql += opts?.additionalSelection;
  }

  return { selection: gql };
};
